import {
  Button, Checkbox, Select,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  useCallback, useContext, useEffect, useMemo, useState, ReactNode,
} from 'react';
import { Product, Role } from 'types/app.types';
import { GeoLayer, Layer, Workspace } from 'types/geoserver.types';
import ProductsContext from 'contexts/ProductsContext';
import { DeleteOutlined } from '@ant-design/icons';
import { useGetCatalogQuery } from 'services/dtn';
import { DTN_TILE_PROVIDER_ENABLED } from 'configs/endpoints';
import { DTNCatalog } from 'services/dtn/types';
import styles from './index.module.scss';
import MapViewContext from '../../../../../../../../../../../../../../contexts/MapViewContext';
import MainAppContext from '../../../../../../../../../../../../../../contexts/MainAppContext';

const { wrapper, dropdownItem, dropdownGroupLabel } = styles;

interface ProductsSelectorProps {
  product: Product,
  onSelectChange: (value: string, layer: Layer) => void
  onCheckboxChange: (state: CheckboxChangeEvent) => void
}

interface RenderOpts {
  itemClass: string;
  groupClass: string;
  filter: string;
}

function renderGeoServerNodes(
  workspaces: Workspace[],
  onPick: (value: string, layer: Layer) => void,
  opts: RenderOpts,
): ReactNode[] {
  return workspaces
    .filter((ws) => ws.name !== 'DTN')
    .flatMap((ws) => {
      const layers = ws.layers ?? [];
      const filtered = opts.filter
        ? layers.filter((l) => l.name.toLowerCase().includes(opts.filter.toLowerCase()))
        : layers;
      if (filtered.length === 0) return [];
      return [
        <div key={`ws-${ws.name}`} className={opts.groupClass} style={{ paddingLeft: 8 }}>
          {ws.name.toUpperCase()}
        </div>,
        ...filtered.map((layer) => {
          const value = `${ws.name}:${(layer as GeoLayer).path || layer.name}`;
          return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
              key={value}
              className={opts.itemClass}
              style={{ paddingLeft: 16 }}
              onMouseDown={(e) => { e.preventDefault(); onPick(value, layer); }}
            >
              {layer.name}
            </div>
          );
        }),
      ];
    });
}

function renderDTNNodes(
  catalog: DTNCatalog,
  groupId: string | null,
  depth: number,
  onPick: (value: string, layer: Layer) => void,
  opts: RenderOpts,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  const indent = 8 + depth * 8;

  catalog.groups
    .filter((g) => g.parent === groupId)
    .forEach((group) => {
      const children = renderDTNNodes(catalog, group.id, depth + 1, onPick, opts);
      if (children.length === 0) return;
      nodes.push(
        <div key={`g-${group.id}`} className={opts.groupClass} style={{ paddingLeft: indent }}>
          {group.label.toUpperCase()}
        </div>,
      );
      nodes.push(...children);
    });

  catalog.layers
    .filter((l) => l.group === groupId)
    .filter((l) => !opts.filter || l.label.toLowerCase().includes(opts.filter.toLowerCase()))
    .forEach((layer) => {
      const value = `DTN:${layer.id}`;
      const geoLayer: GeoLayer = {
        name: layer.label, href: 'dtn', path: layer.id, type: 'dtn',
      };
      nodes.push(
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          key={value}
          className={opts.itemClass}
          style={{ paddingLeft: indent }}
          onMouseDown={(e) => { e.preventDefault(); onPick(value, geoLayer); }}
        >
          {layer.label}
        </div>,
      );
    });

  return nodes;
}

function ProductsSelector({
  product, onSelectChange, onCheckboxChange,
} : ProductsSelectorProps) {
  const { role } = useContext(MainAppContext);
  const {
    workspaces, loadingWorkspaces, activeProducts, deleteProduct,
  } = useContext(ProductsContext);
  const { removeLayer } = useContext(MapViewContext);
  const { data: dtnCatalog } = useGetCatalogQuery(undefined, { skip: !DTN_TILE_PROVIDER_ENABLED });
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [active, setActive] = useState<boolean>(
    activeProducts.some((index) => index === product.index),
  );
  const [isCheckboxDisabled, setIsCheckboxDisabled] = useState<boolean>(true);

  const handlePick = useCallback((value: string, layer: Layer) => {
    onSelectChange(value, layer);
    setOpen(false);
    setSearchText('');
  }, [onSelectChange]);

  const handleDropdownVisibleChange = (visible: boolean) => {
    setOpen(visible);
    if (!visible) setSearchText('');
  };

  const dropdownNodes = useMemo(() => {
    const opts = { itemClass: dropdownItem, groupClass: dropdownGroupLabel, filter: searchText };
    const nodes: ReactNode[] = [
      ...renderGeoServerNodes(workspaces, handlePick, opts),
    ];

    if (dtnCatalog?.layers?.length) {
      const dtnNodes = renderDTNNodes(dtnCatalog, null, 1, handlePick, opts);
      if (dtnNodes.length > 0) {
        nodes.push(
          <div key="dtn-root" className={dropdownGroupLabel} style={{ paddingLeft: 8 }}>
            DTN
          </div>,
        );
        nodes.push(...dtnNodes);
      }
    }

    return nodes;
  }, [workspaces, dtnCatalog, searchText, handlePick]);

  useEffect(() => {
    setTimeout(() => { setIsCheckboxDisabled(role === Role.SUPERVISOR); }, 1000);
  }, [role]);

  useEffect(() => {
    if (activeProducts) {
      setActive(activeProducts.some((index) => index === product.index));
    }
  }, [activeProducts, product.index]);

  return (
    <div className={wrapper}>
      <Checkbox
        checked={active}
        onChange={(ev) => { setActive(ev.target.checked); onCheckboxChange(ev); }}
        disabled={isCheckboxDisabled}
      />
      <Select
        showSearch
        open={open}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        value={product.layer.name}
        loading={loadingWorkspaces}
        popupMatchSelectWidth={500}
        filterOption={false}
        onSearch={(val) => setSearchText(val)}
        // eslint-disable-next-line react/no-unstable-nested-components
        dropdownRender={() => (
          <div className="native-scrollbar" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {dropdownNodes}
          </div>
        )}
      />
      <Button
        onClick={() => { deleteProduct(product.index); removeLayer(product, true); }}
        icon={<DeleteOutlined />}
        type="text"
        disabled={role === Role.SUPERVISOR}
      />
    </div>
  );
}

export default ProductsSelector;
